import {
  ExternalValidationReportDataFragment,
  GetIngestionDocumentFragment,
  RuleType,
} from '@client/graphql/__generated__/types';
import { createFieldKeyToLabelMapping } from './fieldMapping.utils';
import { ValidationItem, ValidationResult } from './validation.utils';

export interface ValidationItemDiffData {
  fieldKey: string;
  fieldLabel?: string;
  aiCount: number;
  regularCount: number;
  changeType: 'improved' | 'degraded' | 'unchanged';
  reductionCount: number;
  hasAiData: boolean;
}

export interface ValidationItemWithDiff extends ValidationItem {
  diffData?: ValidationItemDiffData[];
}

/**
 * Gets validation results for a specific field from a specified property
 */
function getValidationResultsForField(
  validationData: ExternalValidationReportDataFragment,
  contractDocumentUuid: string,
  fieldKey: string,
  ruleUuid: string,
  propertyKey: 'aiValidationResults' | 'validationResults'
): ValidationResult[] {
  const results: ValidationResult[] = [];

  validationData.documentValidations?.forEach((docValidation) => {
    const validationResults = docValidation[propertyKey] as unknown as
      | ValidationResult[]
      | undefined;

    if (validationResults) {
      validationResults.forEach((result) => {
        if (
          result.contractDocumentUuid === contractDocumentUuid &&
          result.ruleUuid === ruleUuid &&
          (result.fieldMappingKey === fieldKey ||
            result.inputValues?.some((iv) => iv.fieldKey === fieldKey))
        ) {
          results.push(result);
        }
      });
    }
  });

  return results;
}

/**
 * Gets AI validation results for a specific field in a validation item
 */
function getAiValidationResultsForField(
  validationData: ExternalValidationReportDataFragment,
  contractDocumentUuid: string,
  fieldKey: string,
  ruleUuid: string
): ValidationResult[] {
  return getValidationResultsForField(
    validationData,
    contractDocumentUuid,
    fieldKey,
    ruleUuid,
    'aiValidationResults'
  );
}

/**
 * Gets regular validation results for a specific field in a validation item
 */
function getRegularValidationResultsForField(
  validationData: ExternalValidationReportDataFragment,
  contractDocumentUuid: string,
  fieldKey: string,
  ruleUuid: string
): ValidationResult[] {
  return getValidationResultsForField(
    validationData,
    contractDocumentUuid,
    fieldKey,
    ruleUuid,
    'validationResults'
  );
}

/**
 * Calculates AI diff data for a validation item, filtered by specific field matches
 */
export function getValidationItemDiffData(
  validationItem: ValidationItem,
  document: GetIngestionDocumentFragment,
  validationData?: ExternalValidationReportDataFragment | null
): ValidationItemDiffData[] {
  if (
    !validationData ||
    !validationItem.inputValues ||
    !validationItem.contractDocumentUuid
  ) {
    return [];
  }

  const fieldKeyToLabel = createFieldKeyToLabelMapping(document);
  const diffData: ValidationItemDiffData[] = [];

  // Only check fields that are actually used in this validation item
  const validationItemFields = new Set(
    validationItem.inputValues.map((iv) => iv.fieldKey)
  );

  validationItemFields.forEach((fieldKey) => {
    const aiResults = getAiValidationResultsForField(
      validationData,
      validationItem.contractDocumentUuid!,
      fieldKey,
      validationItem.ruleUuid
    );

    const regularResults = getRegularValidationResultsForField(
      validationData,
      validationItem.contractDocumentUuid!,
      fieldKey,
      validationItem.ruleUuid
    );

    // Filter to only include results that match this specific validation item's context
    const aiResultsForThisItem = aiResults.filter(
      (r) => r.ruleUuid === validationItem.ruleUuid
    );
    const regularResultsForThisItem = regularResults.filter(
      (r) => r.ruleUuid === validationItem.ruleUuid
    );

    const aiErrorsCount = aiResultsForThisItem.filter(
      (r) => r.output && r.ruleType !== RuleType.WARNING
    ).length;
    const regularErrorsCount = regularResultsForThisItem.filter(
      (r) => r.output && r.ruleType !== RuleType.WARNING
    ).length;

    const aiWarningsCount = aiResultsForThisItem.filter(
      (r) => r.output && r.ruleType === RuleType.WARNING
    ).length;
    const regularWarningsCount = regularResultsForThisItem.filter(
      (r) => r.output && r.ruleType === RuleType.WARNING
    ).length;

    const aiCount = aiErrorsCount + aiWarningsCount;
    const regularCount = regularErrorsCount + regularWarningsCount;
    const reductionCount = regularCount - aiCount;

    let changeType: 'improved' | 'degraded' | 'unchanged' = 'unchanged';
    if (reductionCount > 0) {
      changeType = 'improved';
    } else if (reductionCount < 0) {
      changeType = 'degraded';
    }

    const hasAiData = aiResultsForThisItem.length > 0;

    // Only include fields that have AI data and show differences for this specific validation item
    if (hasAiData && reductionCount !== 0) {
      diffData.push({
        fieldKey,
        fieldLabel: fieldKeyToLabel[fieldKey] || fieldKey,
        aiCount,
        regularCount,
        changeType,
        reductionCount,
        hasAiData,
      });
    }
  });

  return diffData;
}

/**
 * Checks if a validation item has any AI differences
 */
export function hasValidationItemDifferences(
  validationItem: ValidationItem,
  document: GetIngestionDocumentFragment,
  validationData?: ExternalValidationReportDataFragment | null
): boolean {
  const diffData = getValidationItemDiffData(
    validationItem,
    document,
    validationData
  );

  return diffData.some((diff) => diff.changeType !== 'unchanged');
}
